import React, { useState } from 'react';
import { CalendarDays, Users, Clock, MessageCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { CONTACT, EVENT_TYPES, GUEST_COUNTS, BUDGETS } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

const HIGHLIGHTS = [
  { icon: CalendarDays, title: 'Flexible Dates', desc: 'Choose from various available dates' },
  { icon: Users, title: 'Any Size', desc: 'From intimate to grand celebrations' },
  { icon: Clock, title: 'Quick Response', desc: 'We respond within 24 hours' }
];

const initialForm = { name: '', email: '', phone: '', eventType: '', date: '', guests: '', budget: '', details: '' };

export default function PlanEvent() {
  useScrollReveal();
  const [form, setForm] = useState(initialForm);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.eventType || !form.date || !form.guests) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const lines = [
      'New Event Enquiry — Vivamore',
      '',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Event Type: ${form.eventType}`,
      `Preferred Date: ${form.date}`,
      `Expected Guests: ${form.guests}`,
      form.budget ? `Budget: ${form.budget}` : null,
      form.details ? `Additional Details: ${form.details}` : null
    ]
      .filter(Boolean)
      .join('\n');
    const subject = `Event Enquiry — ${form.name} (${form.eventType})`;
    const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
    window.location.href = mailto;
    toast.success('Opening your email app — just tap Send to submit your enquiry.');
    setForm(initialForm);
  };

  const inputCls = 'w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-gold transition-colors font-light';
  const labelCls = 'text-xs tracking-widest uppercase text-ink/60 mb-2 block';

  return (
    <div>
      <Seo
        title="Plan Your Event — Enquiry Form"
        path="/plan-event"
        description="Plan your celebration at Vivamore Dining. Tell us your event type, date, guest count and budget — group reservations and customised micro-events at NU Sentral, KL."
        keywords="plan event KL, event enquiry Vivamore, group reservation KL, micro event venue, book event space NU Sentral"
      />
      <section className="relative pt-40 pb-20 bg-ink overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-gold-light text-xs tracking-luxe uppercase">Get Started</span>
          <h1 className="font-display text-5xl md:text-6xl text-cream mt-5">Plan Your Event</h1>
          <p className="text-cream/75 font-light text-lg mt-5">Let us help you create an unforgettable celebration</p>
        </div>
      </section>

      <section className="bg-cream py-16">
        <div className="max-w-5xl mx-auto px-6 grid sm:grid-cols-3 gap-6 -mt-28 relative z-10">
          {HIGHLIGHTS.map((h, i) => (
            <div key={h.title} className="reveal bg-white shadow-md p-8 text-center" style={{ transitionDelay: `${i * 0.1}s` }}>
              <h.icon size={28} className="text-gold mx-auto" />
              <h3 className="font-serif text-xl text-ink mt-4">{h.title}</h3>
              <p className="text-ink/60 font-light text-sm mt-2">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-28 bg-cream">
        <div className="max-w-3xl mx-auto px-6">
          <div className="reveal text-center mb-12">
            <h2 className="font-display text-4xl text-ink">Event Inquiry Form</h2>
            <div className="gold-line mx-auto mt-6 mb-4" />
            <p className="text-ink/60 font-light">Fill out the form below and our team will get back to you shortly</p>
          </div>

          <form onSubmit={handleSubmit} className="reveal bg-white shadow-sm p-8 md:p-12 space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input className={inputCls} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className={labelCls}>Email Address *</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Phone Number *</label>
                <input className={inputCls} value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+60 __ ___ ____" />
              </div>
              <div>
                <label className={labelCls}>Event Type *</label>
                <select className={inputCls} value={form.eventType} onChange={(e) => update('eventType', e.target.value)}>
                  <option value="">Select event type</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Preferred Date *</label>
                <input type="date" className={inputCls} value={form.date} onChange={(e) => update('date', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Expected Guests *</label>
                <select className={inputCls} value={form.guests} onChange={(e) => update('guests', e.target.value)}>
                  <option value="">Select guest count</option>
                  {GUEST_COUNTS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Estimated Budget (Optional)</label>
                <select className={inputCls} value={form.budget} onChange={(e) => update('budget', e.target.value)}>
                  <option value="">Select budget range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Additional Details</label>
                <textarea rows={4} className={inputCls} value={form.details} onChange={(e) => update('details', e.target.value)} placeholder="Tell us more about your vision..." />
              </div>
            </div>
            <button type="submit" className="btn-gold w-full inline-flex items-center justify-center gap-2 text-xs tracking-widest uppercase px-8 py-4 bg-gold text-cream border border-gold hover:bg-ink hover:border-ink">
              <Mail size={16} /> Submit Enquiry
            </button>
          </form>

          <p className="reveal text-center text-ink/60 font-light mt-8">
            Or contact us directly via WhatsApp at{' '}
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">
              <MessageCircle size={15} /> {CONTACT.phone}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
